/**
 * Per-environment configuration for Sandpiper Management.
 *
 * Each environment has its own API server and its own Cognito auth stack.
 * Cognito values come from each environment's authentication.yml stack outputs
 * (populated by fetch-env.js or manually).
 *
 * These are compile-time constants via Vite env vars. The VITE_ENV_* prefix
 * groups related variables per environment.
 */

export interface CognitoConfig {
  domain: string;
  clientId: string;
  scopes: string;
  identityProvider: string;
}

export interface Environment {
  key: string;
  label: string;
  apiUrl: string;
  cognito: CognitoConfig;
}

const env: Record<string, string> = import.meta.env;

interface EnvDefaults {
  apiUrl?: string;
  cognitoDomain?: string;
  cognitoClientId?: string;
  cognitoScopes?: string;
  cognitoIdp?: string;
}

/**
 * Build an environment config entry from VITE_ env vars.
 * Convention: VITE_ENV_{ENV_KEY}_{FIELD}
 */
const envEntry = (key: string, label: string, defaults: EnvDefaults = {}): Environment => ({
  key,
  label,
  apiUrl: env[`VITE_ENV_${key}_API_URL`] || defaults.apiUrl || '',
  cognito: {
    domain: env[`VITE_ENV_${key}_COGNITO_DOMAIN`] || defaults.cognitoDomain || '',
    clientId: env[`VITE_ENV_${key}_COGNITO_CLIENT_ID`] || defaults.cognitoClientId || '',
    scopes: env[`VITE_ENV_${key}_COGNITO_SCOPES`] || defaults.cognitoScopes || 'openid email profile',
    identityProvider: env[`VITE_ENV_${key}_COGNITO_IDP`] || defaults.cognitoIdp || '',
  },
});

/**
 * All configured environments. Only environments with a non-empty apiUrl
 * AND cognitoDomain are considered fully configured.
 */
const ALL_ENVIRONMENTS: Environment[] = [
  envEntry('PROD', 'Production', {
    apiUrl: 'https://manage.sandpiper.mytransit.info',
  }),
  envEntry('STG', 'Staging', {
    apiUrl: 'https://manage.sandpiper.stg.mytransit.info',
  }),
  envEntry('UAT', 'UAT', {
    apiUrl: 'https://manage.sandpiper.uat.mytransit.info',
  }),
  envEntry('DEV', 'Development', {
    apiUrl: 'https://manage.sandpiper.dev.mytransit.info',
  }),
];

/**
 * Get all environments that have at least an API URL configured.
 */
export const getEnvironments = (): Environment[] =>
  ALL_ENVIRONMENTS.filter((e) => e.apiUrl);

/**
 * Get fully configured environments (API + Cognito).
 */
export const getAuthEnvironments = (): Environment[] =>
  ALL_ENVIRONMENTS.filter((e) => e.apiUrl && e.cognito.domain && e.cognito.clientId);

/**
 * Find an environment config by its key.
 */
export const getEnvironment = (key: string): Environment | null =>
  ALL_ENVIRONMENTS.find((e) => e.key === key) || null;

/**
 * Find which environment matches a given API URL.
 */
export const getEnvironmentByApiUrl = (apiUrl: string): Environment | null => {
  const normalized = (apiUrl || '').replace(/\/+$/, '');
  return ALL_ENVIRONMENTS.find(
    (e) => e.apiUrl.replace(/\/+$/, '') === normalized
  ) || null;
};

/**
 * Get the default environment (first fully configured one, or first with an API URL).
 */
export const getDefaultEnvironment = (): Environment | null =>
  getAuthEnvironments()[0] || getEnvironments()[0] || null;
